import { computeUnderfunded } from './calc'
import type { BudgetCategory, BudgetMonth, CategoryGroup } from './types'

/**
 * Month rollover — mirrors `Budget/Services/BudgetCalculators.swift`
 * (Rollover) + `MonthRolloverRunner.swift`, YNAB mode:
 *   - positive category available carries forward as next month's
 *     category carryOver
 *   - cash overspending does NOT carry on the category; it reduces
 *     next month's TBA (we have no CC-split data on web yet, so all
 *     overspend is treated as cash — same as iOS's default call)
 *
 * Divergence from iOS (intentional, iOS bug tracked in parity
 * ledger): the new month's categories start with assigned = 0 and
 * activity = 0. iOS currently copies the prior month's values in,
 * which double-counts them against the rolled carryOver.
 */

function round2(n: number): number {
  return Math.round(n * 100) / 100
}

// `available` is the persisted user-facing balance (see calc.ts header);
// imported docs hold the leftover there with carryOver 0, so trust it
// over re-deriving from carryOver + assigned + activity.
function calculatedAvailable(c: BudgetCategory): number {
  return c.available
}

function monthTBA(m: BudgetMonth): number {
  const assigned = m.categoryGroups.reduce(
    (acc, g) => acc + g.categories.reduce((a, c) => a + c.assigned, 0),
    0,
  )
  return (m.carryOver ?? 0) + m.income - assigned
}

interface Rollovers {
  byCategory: Map<string, number>
  cashOverspendDeducted: number
}

function calculateRollovers(prev: BudgetMonth): Rollovers {
  const byCategory = new Map<string, number>()
  let cash = 0
  for (const g of prev.categoryGroups) {
    for (const c of g.categories) {
      const avail = round2(calculatedAvailable(c))
      if (avail >= 0) {
        byCategory.set(c.id, avail)
      } else {
        byCategory.set(c.id, 0)
        cash += -avail
      }
    }
  }
  return { byCategory, cashOverspendDeducted: round2(cash) }
}

/** Build a fresh (year, month) from the prior month: categories and
 *  goals copied, assigned/activity zeroed, carryOvers applied. */
export function createNextMonth(prev: BudgetMonth, year: number, month: number): BudgetMonth {
  const { byCategory, cashOverspendDeducted } = calculateRollovers(prev)
  const carryOver = round2(monthTBA(prev) - cashOverspendDeducted)
  const groups: CategoryGroup[] = prev.categoryGroups.map((g) => ({
    ...g,
    categories: g.categories.map((c) => {
      const rolled = round2(byCategory.get(c.id) ?? 0)
      const next: BudgetCategory = {
        ...c,
        assigned: 0,
        activity: 0,
        available: rolled,
        carryOver: rolled,
      }
      return { ...next, targetUnderfunded: computeUnderfunded(next, year, month) }
    }),
  }))
  return {
    id: crypto.randomUUID(),
    year,
    month,
    categoryGroups: groups,
    income: 0,
    budgeted: 0,
    activity: 0,
    available: carryOver,
    carryOver,
    ageOfMoney: prev.ageOfMoney ?? null,
  }
}

/** Re-derive an EXISTING next month's carryOvers from its (changed)
 *  prior month, preserving the next month's own assignments,
 *  activity, and goals. */
export function cascadeInto(prev: BudgetMonth, next: BudgetMonth): BudgetMonth {
  const { byCategory, cashOverspendDeducted } = calculateRollovers(prev)
  const carryOver = round2(monthTBA(prev) - cashOverspendDeducted)
  const groups: CategoryGroup[] = next.categoryGroups.map((g) => ({
    ...g,
    categories: g.categories.map((c) => {
      const newCarry = round2(byCategory.get(c.id) ?? 0)
      const merged: BudgetCategory = {
        ...c,
        carryOver: newCarry,
        available: round2(c.available - c.carryOver + newCarry),
      }
      return { ...merged, targetUnderfunded: computeUnderfunded(merged, next.year, next.month) }
    }),
  }))
  const totalAssigned = groups.reduce(
    (acc, g) => acc + g.categories.reduce((a, c) => a + c.assigned, 0),
    0,
  )
  return {
    ...next,
    categoryGroups: groups,
    carryOver,
    available: round2(carryOver + next.income - totalAssigned),
  }
}
